import { Alert } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomEmpty, CustomConfirmationModal } from "sipli-fleet-core/components";
import Loader from "sipli-fleet-core/components/CustomSharedLoader/CustomSharedLoader";
import {
  useMutationDeleteWarehouseResource,
  useMutationUpdateWarehouseResource,
} from "src/queries/warehouses.queries";
import type {
  WarehouseResource,
  WarehouseResourcePayload,
  WarehouseResourceType,
} from "src/services/warehouseService";
import Notification from "src/util/libs/notification";
import { hierarchyConfig } from "../hierarchyConfig";
import WarehouseResourceCard from "./WarehouseResourceCard";
import WarehouseResourceModal from "./WarehouseResourceModal";

interface WarehouseResourceGridProps {
  resource: WarehouseResourceType;
  resources: WarehouseResource[];
  isLoading: boolean;
  isError: boolean;
  parentPayload?: Partial<WarehouseResourcePayload>;
  editingRecord: WarehouseResource | null;
  modalOpen: boolean;
  onModalClose: () => void;
  onEdit: (record: WarehouseResource) => void;
  onOpen?: (record: WarehouseResource) => void;
}

const WarehouseResourceGrid = ({
  resource,
  resources,
  isLoading,
  isError,
  parentPayload,
  editingRecord,
  modalOpen,
  onModalClose,
  onEdit,
  onOpen,
}: WarehouseResourceGridProps) => {
  const { t } = useTranslation();
  const config = hierarchyConfig[resource];
  const updateMutation = useMutationUpdateWarehouseResource(resource);
  const deleteMutation = useMutationDeleteWarehouseResource(resource);
  const [statusRecordId, setStatusRecordId] = useState<
    string | number | null
  >(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] =
    useState<WarehouseResource | null>(null);

  const handleStatusChange = async (
    record: WarehouseResource,
    active: boolean,
  ) => {
    setStatusRecordId(record.id);

    try {
      await updateMutation.mutateAsync({
        id: record.id,
        active,
      });
      Notification.success({
        message: t("status_updated_successfully"),
      });
    } catch (error: unknown) {
      const responseMessage = (error as any)?.response?.data?.message;

      if (!responseMessage) {
        Notification.error({
          message: t("error"),
          description: t("status_update_failed"),
        });
      }
    } finally {
      setStatusRecordId(null);
    }
  };

  const handleDelete = (record: WarehouseResource) => {
    setRecordToDelete(record);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      await deleteMutation.mutateAsync(recordToDelete.id);
      Notification.success({
        message: t("warehouse_resource_deleted", {
          resource: t(config.singularKey),
        }),
      });
      setDeleteConfirmOpen(false);
      setRecordToDelete(null);
    } catch (error: unknown) {
      const responseMessage = (error as any)?.response?.data?.message;

      Notification.error({
        message: t("error"),
        description:
          responseMessage ||
          t("warehouse_resource_delete_failed", {
            resource: t(config.singularKey),
          }),
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setRecordToDelete(null);
  };

  if (isLoading) {
    return <Loader height="h-64" />;
  }

  if (isError) {
    return (
      <Alert type="error" showIcon message={t("warehouse_load_error")} />
    );
  }

  return (
    <>
      {resources.length === 0 ? (
        <div className="rounded-[16px] bg-white px-6 py-20">
          <CustomEmpty description={t(config.emptyKey)} />
        </div>
      ) : (
        <div className="warehouse-resource-grid">
          {resources.map((record) => (
            <WarehouseResourceCard
              key={record.id}
              resource={resource}
              record={record}
              statusLoading={
                updateMutation.isPending && statusRecordId === record.id
              }
              onOpen={onOpen ? () => onOpen(record) : undefined}
              onEdit={() => onEdit(record)}
              onDelete={() => handleDelete(record)}
              onStatusChange={(active) =>
                handleStatusChange(record, active)
              }
            />
          ))}
        </div>
      )}

      <WarehouseResourceModal
        open={modalOpen}
        resource={resource}
        record={editingRecord}
        parentPayload={parentPayload}
        onClose={onModalClose}
      />

      <CustomConfirmationModal
        open={deleteConfirmOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
        title={t("delete_resource_title", {
          resource: t(config.singularKey),
        })}
        description={t("delete_resource_description", {
          name: recordToDelete?.name ?? "",
        })}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        danger
      />
    </>
  );
};

export default WarehouseResourceGrid;
